import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { notification as antdNotification } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import { useAuth } from "./AuthContext";
import statutoryStageChatService from "../services/statutoryStageChatService";

// Notification sound - simple beep
const NOTIFICATION_SOUND_URL =
  "data:audio/wav;base64,UklGRl9vT19teleQYAQKPd7qJlAQBBqODxnF0AAEKs4/OYVwAARK/l9JRRAQBGsuf1kEsBAEi06/aNRQEASrbt+IlAAQBMuO/5hTsBAE667vmBNgEAULzw+n0xAQBSvvH7eSsBAFS/8vt1JgEAVsHz/HEhAQBYwvT8bRwBAFrD9f1pFwEAXMT2/WUSAQBexff9YQ0BAF/G+P5dCAEAYcf5/lkDAQBjyPr+VQABAGTJe/5RAQEAZsp8/k0BAQBoy33+SQEBAGnMfv5FAQEAa81//kEBAQBszn/+PQEBAGzPgP45AQEAbdCA/jUBAQBu0YH+MQEBAHDSgv4tAQEAcdOC/ikBAQBy1IP+JQEBAHPVhP4hAQEAdNaE/h0BAQBz1oT+GQEBAHPWhP4VAQEAc9aE/hEBAQBz1oT+DQEBAHPWhP4JAQEAc9aE/gUBAQBz1oT+AQEBAHPWBP4AAQEA";

const playNotificationSound = () => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Ignore - user may not have interacted with page yet
    });
  } catch {
    // Ignore audio errors
  }
};

export interface ChatNotification {
  id: string;
  partyId: string;
  stageId: string;
  senderName: string;
  messagePreview: string;
  timestamp: Date;
}

interface StatutoryStageChatContextType {
  // Unread message count for the current party/stage
  unreadCount: number;
  // Refresh unread count
  refreshUnreadCount: () => Promise<void>;
  // Clear unread count (when user opens chat)
  clearUnreadCount: () => void;
  // Connection status
  isConnected: boolean;
  // Latest notification for display
  latestNotification: ChatNotification | null;
  // Current party/stage being tracked
  currentPartyId: string | null;
  currentStageId: string | null;
  // Set current stage (for updating when navigating between statutory stages)
  setCurrentStage: (partyId: string, stageId: string) => void;
  // Sync from localStorage
  syncFromLocalStorage: () => void;
  // Toggle chat drawer
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  // Track if user is on the chat page (not drawer)
  isChatPageActive: boolean;
  setChatPageActive: (active: boolean) => void;
}

const StatutoryStageChatContext = createContext<StatutoryStageChatContextType | undefined>(undefined);

const WS_PATH = "/ws/statutory-chat";
const RECONNECT_DELAY = 5000;
const PING_INTERVAL = 30000;
const UNREAD_POLL_INTERVAL = 60000; // Poll every 60 seconds as backup

const buildWebSocketUrl = (
  partyId: string,
  stageId: string,
  token: string
): string | null => {
  try {
    const apiUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      console.error("[StatutoryStageChatContext] No API URL configured");
      return null;
    }

    const url = new URL(apiUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = WS_PATH;
    url.search = new URLSearchParams({ token, partyId, stageId }).toString();
    
    return url.toString();
  } catch (error) {
    console.error("[StatutoryStageChatContext] Failed to build WebSocket URL:", error);
    return null;
  }
};

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("accessToken");
};

interface WebSocketEvent {
  type: "chat.message" | "chat.notification" | "chat.connected" | "pong";
  data?: {
    partyId?: string;
    stageId?: string;
    senderUserId?: string;
    senderName?: string;
    messagePreview?: string;
    timestamp?: string;
  };
}

export const StatutoryStageChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState<ChatNotification | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatPageActive, setIsChatPageActive] = useState(false);
  const [currentPartyId, setCurrentPartyId] = useState<string | null>(null);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const unreadPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  // Get current party/stage from localStorage
  // Use selectedStatutoryStageId for the actual statutory stage (not selectedStageId which is the main stage constant)
  const syncFromLocalStorage = useCallback(() => {
    const partyId = localStorage.getItem("selectedPartyId");
    const stageId = localStorage.getItem("selectedStatutoryStageId");
    setCurrentPartyId(partyId);
    setCurrentStageId(stageId);
  }, []);

  // Initialize from localStorage and listen for changes
  useEffect(() => {
    syncFromLocalStorage();

    // StorageEvent only fires for cross-tab changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyId" || event.key === "selectedStatutoryStageId") {
        syncFromLocalStorage();
      }
    };

    // Custom event for same-tab localStorage changes
    const handleLocalStorageUpdate = () => {
      syncFromLocalStorage();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("statutoryStageChanged", handleLocalStorageUpdate);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("statutoryStageChanged", handleLocalStorageUpdate);
    };
  }, [syncFromLocalStorage]);

  const refreshUnreadCount = useCallback(async () => {
    if (!currentPartyId || !currentStageId) {
      setUnreadCount(0);
      return;
    }

    try {
      const result = await statutoryStageChatService.getUnreadCount(
        currentPartyId,
        currentStageId
      );
      if (mountedRef.current) {
        setUnreadCount(result.unreadCount);
      }
    } catch (error) {
      console.error("[StatutoryStageChatContext] Failed to fetch unread count:", error);
    }
  }, [currentPartyId, currentStageId]);

  const clearUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const openChat = useCallback(() => {
    setIsChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);

  // Set current stage directly (for same-tab navigation)
  const setCurrentStage = useCallback((partyId: string, stageId: string) => {
    setCurrentPartyId(partyId);
    setCurrentStageId(stageId);
  }, []);

  // Callback to set chat page active state
  const setChatPageActive = useCallback((active: boolean) => {
    setIsChatPageActive(active);
  }, []);

  // Refs for notification handler to avoid reconnection loops
  const isChatOpenRef = useRef(isChatOpen);
  const isChatPageActiveRef = useRef(isChatPageActive);
  const currentPartyIdRef = useRef(currentPartyId);
  const currentStageIdRef = useRef(currentStageId);
  const userIdRef = useRef(user?.id);

  // Keep refs in sync
  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  useEffect(() => {
    isChatPageActiveRef.current = isChatPageActive;
  }, [isChatPageActive]);

  useEffect(() => {
    currentPartyIdRef.current = currentPartyId;
  }, [currentPartyId]);

  useEffect(() => {
    currentStageIdRef.current = currentStageId;
  }, [currentStageId]);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  // Handle incoming WebSocket notifications - uses refs to avoid dependency changes
  const handleNotification = useCallback(
    (data: WebSocketEvent["data"]) => {
      if (!data?.partyId || !data?.stageId) return;

      // Check if user is viewing chat (either drawer open or on chat page)
      const isViewingChat = isChatOpenRef.current || isChatPageActiveRef.current;

      // Only increment if it's for the current party/stage and not from current user
      if (
        data.partyId === currentPartyIdRef.current &&
        data.stageId === currentStageIdRef.current &&
        data.senderUserId !== userIdRef.current
      ) {
        // Don't increment if chat is open or user is on chat page - user will see message in real-time
        if (!isViewingChat) {
          setUnreadCount((prev) => prev + 1);
        }

        const notification: ChatNotification = {
          id: `${data.partyId}-${data.stageId}-${Date.now()}`,
          partyId: data.partyId,
          stageId: data.stageId,
          senderName: data.senderName || "Someone",
          messagePreview: data.messagePreview || "",
          timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
        };

        setLatestNotification(notification);

        // Play sound only if chat is not open and user is not on chat page
        if (!isViewingChat) {
          playNotificationSound();

          // Show toast
          antdNotification.info({
            message: "New discussion message",
            description: (
              <div>
                <strong>{data.senderName}</strong>: {data.messagePreview || "sent a message"}
              </div>
            ),
            icon: <MessageOutlined style={{ color: "#1890ff" }} />,
            placement: "topRight",
            duration: 6,
            key: `statutory-chat-${Date.now()}`,
            onClick: () => {
              setIsChatOpen(true);
            },
          });
        }

        // Clear latest notification after delay
        setTimeout(() => {
          setLatestNotification((current) =>
            current?.id === notification.id ? null : current
          );
        }, 10000);
      }
    },
    [] // No dependencies - uses refs instead
  );

  // WebSocket connection management - uses refs to avoid dependency on handleNotification
  const connect = useCallback(() => {
    if (!isAuthenticated || !mountedRef.current || !currentPartyId || !currentStageId) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      return;
    }

    const wsUrl = buildWebSocketUrl(currentPartyId, currentStageId, token);
    if (!wsUrl) {
      return;
    }

    // Close existing connection
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        if (mountedRef.current) {
          setIsConnected(true);
          // Start ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          pingIntervalRef.current = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ type: "ping" }));
            }
          }, PING_INTERVAL);
        }
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === "chat.notification") {
            handleNotification(payload.data);
          } else if (payload.type === "chat.message" && payload.data) {
            // chat.message has different structure - normalize it for notification handling
            const messageData = payload.data;
            handleNotification({
              partyId: messageData.partyId,
              stageId: messageData.stageId,
              senderUserId: messageData.createdBy,
              senderName: messageData.createdByName || messageData.createdByEmail || "Someone",
              messagePreview: messageData.message || (messageData.hasAttachment ? "Shared a file" : ""),
              timestamp: messageData.createdAt,
            });
          }
        } catch {
          // Ignore parse errors
        }
      };

      socket.onerror = () => {
        if (mountedRef.current) {
          setIsConnected(false);
        }
      };

      socket.onclose = () => {
        if (mountedRef.current) {
          setIsConnected(false);
          // Clear ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          // Attempt reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              connect();
            }
          }, RECONNECT_DELAY);
        }
      };
    } catch (error) {
      console.error("[StatutoryStageChatContext] WebSocket connection failed:", error);
      setIsConnected(false);
    }
  }, [isAuthenticated, currentPartyId, currentStageId, handleNotification]);

  // Connect when authenticated and party/stage changes
  useEffect(() => {
    if (isAuthenticated && currentPartyId && currentStageId) {
      connect();
      refreshUnreadCount();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, currentPartyId, currentStageId, connect, refreshUnreadCount]);

  // Poll for unread count as backup
  useEffect(() => {
    if (currentPartyId && currentStageId) {
      unreadPollIntervalRef.current = setInterval(() => {
        if (!isChatOpen) {
          refreshUnreadCount();
        }
      }, UNREAD_POLL_INTERVAL);
    }

    return () => {
      if (unreadPollIntervalRef.current) {
        clearInterval(unreadPollIntervalRef.current);
        unreadPollIntervalRef.current = null;
      }
    };
  }, [currentPartyId, currentStageId, isChatOpen, refreshUnreadCount]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const value = useMemo<StatutoryStageChatContextType>(
    () => ({
      unreadCount,
      refreshUnreadCount,
      clearUnreadCount,
      isConnected,
      latestNotification,
      currentPartyId,
      currentStageId,
      setCurrentStage,
      syncFromLocalStorage,
      isChatOpen,
      openChat,
      closeChat,
      toggleChat,
      isChatPageActive,
      setChatPageActive,
    }),
    [
      unreadCount,
      refreshUnreadCount,
      clearUnreadCount,
      isConnected,
      latestNotification,
      currentPartyId,
      currentStageId,
      setCurrentStage,
      syncFromLocalStorage,
      isChatOpen,
      openChat,
      closeChat,
      toggleChat,
      isChatPageActive,
      setChatPageActive,
    ]
  );

  return (
    <StatutoryStageChatContext.Provider value={value}>
      {children}
    </StatutoryStageChatContext.Provider>
  );
};

export const useStatutoryStageChat = (): StatutoryStageChatContextType => {
  const context = useContext(StatutoryStageChatContext);
  if (!context) {
    throw new Error(
      "useStatutoryStageChat must be used within a StatutoryStageChatProvider"
    );
  }
  return context;
};

export default StatutoryStageChatContext;
